import { Badge } from '@sea-snag/ui';

export const BOAT_KINDS: Record<string, string> = {
  sailing_yacht: 'Sailing Yacht',
  motor_yacht: 'Motor Yacht',
  catamaran: 'Catamaran',
  gulet: 'Gulet',
  power_catamaran: 'Power Catamaran',
  motor_sailer: 'Motor Sailer',
  trimaran: 'Trimaran',
};

export function PositionBadge({ position, pct }: { position: string; pct: number | null }) {
  const classes: Record<string, string> = {
    competitive: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    underpriced: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    overpriced: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };

  return (
    <Badge variant="secondary" className={classes[position] ?? ''}>
      {pct != null ? `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%` : position}
    </Badge>
  );
}

export function FilterTag({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-1.5 rounded-md border px-2 py-1">
      <span className="text-muted-foreground text-xs">{label}:</span>
      <span className="text-xs font-medium">{value}</span>
    </div>
  );
}

export function formatWeekRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  // The dates are UTC days. Formatted in the viewer's zone, everyone west of UTC
  // saw each week a day early, Friday to Friday.
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' };
  return `${s.toLocaleDateString('en', opts)} – ${e.toLocaleDateString('en', opts)}`;
}

export function formatCurrency(value: number, currency = 'EUR'): string {
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

export function getPositionColor(position: string | null): string {
  switch (position) {
    case 'competitive':
      return '#22c55e';
    case 'underpriced':
      return '#f59e0b';
    case 'overpriced':
      return '#ef4444';
    default:
      return 'var(--color-ownPrice)';
  }
}

export function getPositionTextClass(position: string | null): string {
  switch (position) {
    case 'competitive':
      return 'text-green-600';
    case 'underpriced':
      return 'text-amber-600';
    case 'overpriced':
      return 'text-red-600';
    default:
      return '';
  }
}

export interface CharterWeek {
  weekStart: string;
  weekEnd: string;
  label: string;
}

export function getCharterWeeks(year: number): CharterWeek[] {
  const jan1 = new Date(year, 0, 1);
  const dayOfWeek = jan1.getDay();
  const daysUntilSat = (6 - dayOfWeek + 7) % 7;
  const firstSat = new Date(year, 0, 1 + daysUntilSat);

  const weeks: CharterWeek[] = [];
  const endOfYear = new Date(year, 11, 31);
  const current = new Date(firstSat);

  while (current <= endOfYear) {
    const weekEnd = new Date(current);
    weekEnd.setDate(weekEnd.getDate() + 7);

    const weekStart = current.toISOString().split('T')[0]!;
    const weekEndStr = weekEnd.toISOString().split('T')[0]!;
    const label = formatWeekRange(weekStart, weekEndStr);

    weeks.push({ weekStart, weekEnd: weekEndStr, label });
    current.setDate(current.getDate() + 7);
  }

  return weeks;
}

export function getDefaultWeek(weeks: CharterWeek[]): string {
  const today = new Date().toISOString().split('T')[0]!;
  const upcoming = weeks.find((w) => w.weekStart >= today);
  return upcoming?.weekStart ?? weeks[0]?.weekStart ?? '';
}
